import Fade from '@mui/material/Fade';
import Tooltip from '@mui/material/Tooltip';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React from 'react';
import { reportEvent } from 'utils/analytics';
import { navigateToSignOut } from 'utils/authUtils';
import { INavItem } from 'utils/globalTypes';
import { useMobileDetect } from 'utils/hooks/useMobileDetect';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { filterNavItem, navigateToUrl } from 'utils/navigateUtils';
import { sameParent, sameUrl } from 'utils/urlUtils';

import s from './SideBar.module.scss';
import SideBarAdmin from './SideBarAdmin';

interface ISideBar {
  navItems: Array<INavItem>;
  isManagement?: boolean;
}

interface RenderNavButton {
  hId: number;
  id: number;
  name: string;
  url: string;
  open?: boolean;
  basePath?: string;
  JsxIcon?: React.ElementType;
}

const SideBar = ({ navItems, isManagement }: ISideBar) => {
  const router = useRouter();

  const { isMobile, isTablet } = useMobileDetect();
  const authDetails = useUserAuth();

  const onNavigate = async (url: string) => {
    await navigateToUrl(url);
  };

  const hasActive = !!navItems.filter(({ url }) => sameParent(router, url)).length;
  if (!hasActive) {
    return null;
  }

  const onSignOut = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    navigateToSignOut();
  };

  const onAction = (event: React.MouseEvent<HTMLElement>, name: string, url?: string) => {
    if (url === 'sign_out') {
      return onSignOut(event);
    }

    reportEvent(name, { url: url });
    url && onNavigate(url);
  };

  const renderNavButton = ({ open, hId, id, name, url, basePath, JsxIcon }: RenderNavButton) => (
    <button
      key={`nav-item-${hId}-${id}`}
      data-testid={`nav_item_${hId}_${id}`}
      className={cn(s.SideBar__contentItem, {
        isActive: sameUrl(router, url) || (basePath && router?.pathname.startsWith(basePath)),
        isIcon: !!JsxIcon
      })}
      onClick={(event: React.MouseEvent<HTMLElement>) => onAction(event, name, url)}
    >
      {isManagement ? (
        <>
          {JsxIcon && !open && (
            <Fade timeout={800} in={!open}>
              <JsxIcon />
            </Fade>
          )}
          {open && (
            <Fade in={open} timeout={800}>
              <div>{name}</div>
            </Fade>
          )}
        </>
      ) : (
        <span>{name}</span>
      )}
    </button>
  );

  const renderFlatItems = (open?: boolean) =>
    navItems
      .filter(({ url }) => sameParent(router, url))
      .flatMap(({ name, url, menuItems }, hId) => {
        return [
          menuItems
            ?.filter((item) => filterNavItem(item, authDetails))
            ?.map(({ name, url, basePath, JsxIcon }, id) => {
              return isManagement ? (
                open ? (
                  renderNavButton({ open, hId, id, name, url, basePath })
                ) : (
                  <Tooltip key={`nav-item-${hId}-${id}`} title={name} placement="right">
                    {renderNavButton({ open, hId, id, name, url, basePath, JsxIcon })}
                  </Tooltip>
                )
              ) : (
                renderNavButton({ hId, id, name, url, basePath })
              );
            })
        ];
      })
      .flat();

  return isManagement ? (
    <SideBarAdmin rednerItems={renderFlatItems} />
  ) : (
    <div
      className={cn(s.SideBar, {
        isMobile,
        isTablet
      })}
    >
      <nav className={s.SideBar__content} data-testid="SideBar_content">
        {renderFlatItems()}
      </nav>
    </div>
  );
};

export default observer(SideBar);
